import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@shared/session/store';
import { Button, Dialog, FormError, PasswordField, TextField } from '@shared/ui';
import { authApi } from '../api';
import { useStepUp } from '../model/stepUp';

/**
 * Re-authentication for sensitive actions (API 403 STEP_UP_REQUIRED). On success the new access
 * token replaces the old one and the API client replays the original request.
 */
export function StepUpDialog() {
  const { t } = useTranslation(['auth', 'common']);
  const open = useStepUp((s) => s.open);
  const settle = useStepUp((s) => s.settle);
  const mfaEnabled = useSession((s) => Boolean(s.user?.mfa_enabled));
  const setAccessToken = useSession((s) => s.setAccessToken);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword('');
      setCode('');
      setError(null);
    }
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = mfaEnabled ? { totp_code: code.trim() } : { password };
      const response = await authApi.stepUp(body);
      setAccessToken(response.access_token);
      settle(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const passwordForm = (
    <PasswordField
      label={t('auth:login.password')}
      autoComplete="current-password"
      value={password}
      onChange={(e) => setPassword(e.target.value)}
      required
    />
  );
  const codeForm = (
    <TextField
      label={t('auth:mfa.codeLabel')}
      hint={t('auth:mfa.codeHint')}
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]*"
      maxLength={6}
      ltr
      className="max-w-[12rem]"
      value={code}
      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
      required
    />
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) settle(false);
      }}
      title={t('auth:stepUp.title')}
      description={t('auth:stepUp.description')}
      size="sm"
      dismissOnOutside={false}
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <FormError error={error} />
        {/* The API accepts only a TOTP code once MFA is enabled, a password otherwise (auth/service.py step_up). */}
        {mfaEnabled ? codeForm : passwordForm}
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => settle(false)}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" loading={busy}>
            {t('auth:stepUp.confirm')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
